import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';

/**
 * ResizableTable - Таблица с возможностью изменения ширины и порядка колонок
 * - Авто-размер по умолчанию
 * - Ручное изменение ширины колонок (drag resize)
 * - Drag-and-drop для изменения порядка колонок
 * - Сохранение настроек в localStorage
 */

const STORAGE_KEY = 'report-table-columns-config';

// Дефолтные ширины колонок
const DEFAULT_WIDTHS = {
  name: 250,
  client_name: 150,
  responsible_name: 140,
  health_status: 100,
  expected_end_date: 100,
  budget_wo_vat: 130,
  budget_plan: 120,
  budget_fact: 120,
  budget_delta: 120,
  margin_amount: 120,
  margin_percent: 90,
  vendor_name: 120,
  indicators: 100,
};

const ResizableTable = ({
  data,
  columns,
  formatCell,
  storageKey = STORAGE_KEY
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const tableRef = useRef(null);

  // Состояние ширин колонок
  const [columnWidths, setColumnWidths] = useState(() => {
    const saved = localStorage.getItem(`${storageKey}-widths`);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return {};
      }
    }
    return {};
  });

  // Состояние порядка колонок
  const [columnOrder, setColumnOrder] = useState(() => {
    const saved = localStorage.getItem(`${storageKey}-order`);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return null;
      }
    }
    return null;
  });

  // Состояние ресайза
  const [resizing, setResizing] = useState(null);
  const [startX, setStartX] = useState(0);
  const [startWidth, setStartWidth] = useState(0);

  // Column drag-and-drop state
  const [draggedColumn, setDraggedColumn] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  const dragNodeRef = useRef(null);

  // Сохраняем настройки в localStorage
  useEffect(() => {
    localStorage.setItem(`${storageKey}-widths`, JSON.stringify(columnWidths));
  }, [columnWidths, storageKey]);

  useEffect(() => {
    if (columnOrder) {
      localStorage.setItem(`${storageKey}-order`, JSON.stringify(columnOrder));
    }
  }, [columnOrder, storageKey]);

  // Получить ширину колонки (сохранённая или дефолтная)
  const getColumnWidth = useCallback((key) => {
    return columnWidths[key] || DEFAULT_WIDTHS[key] || 120;
  }, [columnWidths]);

  // Упорядочить колонки по сохранённому порядку
  const orderedColumns = useCallback(() => {
    if (!columnOrder || columnOrder.length === 0) {
      return columns;
    }
    // Sort columns by saved order, keeping any new columns at the end
    const ordered = [];
    const remaining = [...columns];

    for (const key of columnOrder) {
      const idx = remaining.findIndex(c => c.key === key);
      if (idx !== -1) {
        ordered.push(remaining[idx]);
        remaining.splice(idx, 1);
      }
    }
    // Add any columns not in saved order
    return [...ordered, ...remaining];
  }, [columns, columnOrder]);

  // === RESIZE HANDLERS ===

  const handleResizeStart = useCallback((e, columnKey) => {
    e.preventDefault();
    e.stopPropagation();

    const currentWidth = getColumnWidth(columnKey);

    setResizing(columnKey);
    setStartX(e.clientX);
    setStartWidth(currentWidth);

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [getColumnWidth]);

  const handleResizeMove = useCallback((e) => {
    if (!resizing) return;

    const diff = e.clientX - startX;
    const newWidth = Math.max(50, startWidth + diff);

    setColumnWidths(prev => ({
      ...prev,
      [resizing]: newWidth
    }));
  }, [resizing, startX, startWidth]);

  const handleResizeEnd = useCallback(() => {
    setResizing(null);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  // Global mouse listeners for resize
  useEffect(() => {
    if (resizing) {
      window.addEventListener('mousemove', handleResizeMove);
      window.addEventListener('mouseup', handleResizeEnd);
      return () => {
        window.removeEventListener('mousemove', handleResizeMove);
        window.removeEventListener('mouseup', handleResizeEnd);
      };
    }
  }, [resizing, handleResizeMove, handleResizeEnd]);

  // Двойной клик - сброс ширины колонки к дефолту
  const handleDoubleClick = useCallback((e, columnKey) => {
    e.preventDefault();
    e.stopPropagation();
    setColumnWidths(prev => {
      const newWidths = { ...prev };
      delete newWidths[columnKey];
      return newWidths;
    });
  }, []);

  // === COLUMN DRAG-AND-DROP HANDLERS ===

  const handleColumnDragStart = useCallback((e, colKey) => {
    setDraggedColumn(colKey);
    const th = e.target.closest('th');
    dragNodeRef.current = th;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', colKey);
    setTimeout(() => {
      if (dragNodeRef.current) {
        dragNodeRef.current.style.opacity = '0.5';
      }
    }, 0);
  }, []);

  const handleColumnDragEnd = useCallback(() => {
    if (dragNodeRef.current) {
      dragNodeRef.current.style.opacity = '1';
    }
    setDraggedColumn(null);
    setDragOverColumn(null);
    dragNodeRef.current = null;
  }, []);

  const handleColumnDragOver = useCallback((e, colKey) => {
    e.preventDefault();
    if (draggedColumn && draggedColumn !== colKey) {
      setDragOverColumn(colKey);
    }
  }, [draggedColumn]);

  const handleColumnDrop = useCallback((e, targetKey) => {
    e.preventDefault();
    if (draggedColumn && draggedColumn !== targetKey) {
      const currentOrder = orderedColumns().map(c => c.key);
      const fromIndex = currentOrder.indexOf(draggedColumn);
      const toIndex = currentOrder.indexOf(targetKey);
      if (fromIndex !== -1 && toIndex !== -1) {
        const newOrder = [...currentOrder];
        const [removed] = newOrder.splice(fromIndex, 1);
        newOrder.splice(toIndex, 0, removed);
        setColumnOrder(newOrder);
      }
    }
    handleColumnDragEnd();
  }, [draggedColumn, orderedColumns, handleColumnDragEnd]);

  // Общая ширина таблицы
  const displayColumns = orderedColumns();
  const totalWidth = 40 + displayColumns.reduce((sum, col) => sum + getColumnWidth(col.key), 0);

  return (
    <div className="overflow-auto relative" style={{ maxHeight: 'calc(100vh - 260px)' }}>
      <table
        ref={tableRef}
        className="text-sm border-collapse"
        style={{
          tableLayout: 'fixed',
          width: totalWidth,
          minWidth: '100%'
        }}
        data-testid="report-table"
      >
        <thead className="bg-gray-50 border-b sticky top-0 z-10 shadow-sm">
          <tr>
            {/* Колонка номера */}
            <th
              className="text-left py-1.5 px-2 text-xs font-medium text-gray-500 whitespace-nowrap bg-gray-50 border-b border-r border-gray-200"
              style={{ width: 40 }}
            >
              №
            </th>

            {displayColumns.map((col, idx) => (
              <th
                key={col.key}
                draggable
                onDragStart={(e) => handleColumnDragStart(e, col.key)}
                onDragEnd={handleColumnDragEnd}
                onDragOver={(e) => handleColumnDragOver(e, col.key)}
                onDragEnter={(e) => { e.preventDefault(); if (draggedColumn) setDragOverColumn(col.key); }}
                onDragLeave={() => setDragOverColumn(null)}
                onDrop={(e) => handleColumnDrop(e, col.key)}
                className={cn(
                  "text-left py-1.5 px-2 text-xs font-medium text-gray-500 bg-gray-50 relative group border-b border-r border-gray-200",
                  "cursor-grab active:cursor-grabbing select-none",
                  resizing === col.key && "bg-blue-50",
                  dragOverColumn === col.key && "bg-blue-100 border-l-2 border-l-blue-500",
                  draggedColumn === col.key && "opacity-50"
                )}
                style={{ width: getColumnWidth(col.key) }}
              >
                <span className="block truncate pr-3">{col.label}</span>

                {/* Resize handle */}
                <div
                  className={cn(
                    "absolute top-0 -right-[3px] w-[6px] h-full cursor-col-resize z-20",
                    resizing === col.key ? "bg-blue-500" : "bg-transparent hover:bg-blue-400"
                  )}
                  onMouseDown={(e) => handleResizeStart(e, col.key)}
                  onDoubleClick={(e) => handleDoubleClick(e, col.key)}
                  onClick={(e) => e.stopPropagation()}
                  title="Потяните для изменения ширины. Двойной клик — сброс"
                />
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {(!data?.projects || data.projects.length === 0) ? (
            <tr>
              <td
                colSpan={displayColumns.length + 1}
                className="py-8 text-center text-gray-400"
              >
                Нет данных
              </td>
            </tr>
          ) : (
            data.projects.map((project, idx) => (
              <tr
                key={project.id}
                className={cn(
                  "border-b hover:bg-gray-50 cursor-pointer transition-colors",
                  project.is_problem && "bg-red-50/50"
                )}
                onClick={() => navigate(`/projects/${project.id}`, { state: { returnUrl: location.pathname + location.search } })}
                data-testid={`report-row-${project.id}`}
              >
                <td className="py-1.5 px-2 text-gray-400 whitespace-nowrap border-r border-gray-100" style={{ width: 40 }}>
                  {idx + 1}
                </td>
                {displayColumns.map(col => (
                  <td
                    key={col.key}
                    className={cn(
                      "py-1.5 px-2 overflow-hidden text-ellipsis whitespace-nowrap border-r border-gray-100",
                      col.key === 'margin_amount' && project.margin_amount < 0 && "text-red-600 font-medium"
                    )}
                    style={{ width: getColumnWidth(col.key) }}
                  >
                    {formatCell(project, col.key)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default ResizableTable;
